import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useLanguage } from '../../context/LanguageContext';
import { i18n } from '../../i18n/i18n';
import AntMascot from '../AntMascot';
import { KidButton, KidCard, StarChip } from '../kid/KidUI';
import { StoredPicture } from '../kid/StoredPicture';
import { starsText } from '../kid/KidTaskCard';
import { pictureTint, TINT_CLASS } from '../kid/kidUiHelpers';
import { hideToast, showToast } from '../kid/Toast';
import { pluralKey } from '../../utils/starGoal';
import { dayRelation } from '../../utils/calendarNav';
import { useViewportWidth } from '../../hooks/useViewportWidth';

/**
 * "Approve tasks" (kid-first redesign step 6 — mock-ups phone-14 / tablet-*-11, decision D7).
 *
 * - The whole picture, who did it and when, steps done, then one big Approve and, kept apart,
 *   "Not done yet" (with a kind preset reason the child sees on the task).
 * - Optional one-tap note ("So neat!") shown on the child's approval screen.
 * - Undo: approving waits ~5 s while the message with Undo shows, and only then sends — so no
 *   stars ever have to be taken back. Leaving the screen sends anything still waiting.
 */

type Note = 'so_neat' | 'well_done' | 'thank_you';
type Reason = 'look_again' | 'steps_missing' | 'do_together';
const NOTES: Note[] = ['so_neat', 'well_done', 'thank_you'];
const REASONS: Reason[] = ['look_again', 'steps_missing', 'do_together'];
const UNDO_MS = 5000;

const initialCircle = (name: string) => (
  <span aria-hidden className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-kid-heading font-extrabold text-kid-text ${TINT_CLASS[pictureTint(name)]}`}>
    {name.trim().charAt(0).toLocaleUpperCase()}
  </span>
);

const ApproveTasks: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { current_user, current_family, verifyTask, updateTask } = useAppContext() as any;
  const { current_language } = useLanguage();
  const navigate = useNavigate();
  const wide = useViewportWidth() >= 1024;
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, Note | undefined>>({});
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const pendingRef = useRef<{ task: any; note: Note | null; timer: number } | null>(null);

  const hide = (id: string) => setHidden((h) => new Set(h).add(id));
  const unhide = (id: string) => setHidden((h) => { const n = new Set(h); n.delete(id); return n; });

  const errorText = (e: any) => {
    const msg = String(e?.message || '');
    return /own task/i.test(msg) ? i18n.t('approve.self_blocked') : msg || 'Error';
  };

  const send = useCallback(async (p: { task: any; note: Note | null }) => {
    try {
      const r = await verifyTask(p.task.id, { approvalNote: p.note });
      if (r?.already_verified) showToast({ message: i18n.t('admin_panel.verify_already_done') });
    } catch (e: any) {
      unhide(p.task.id);
      showToast({ message: errorText(e), tone: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyTask]);

  /** Send any approval still waiting for its Undo window (another approval, leaving, hiding). */
  const flush = useCallback(() => {
    const p = pendingRef.current;
    if (!p) return;
    window.clearTimeout(p.timer);
    pendingRef.current = null;
    void send(p);
  }, [send]);

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      flush(); // leaving the screen: send now rather than lose the approval
    };
  }, [flush]);

  if (!current_user || !current_family) return null;

  const nameOf = (id: string) => current_family.members.find((m: any) => m.id === id)?.name || '';
  const adminCount = current_family.members.filter((m: any) => m.is_admin).length;
  const pending = (current_family.tasks as any[])
    .filter((t) => t.is_completed && !t.verified && !hidden.has(t.id))
    .sort((a, b) => new Date(a.completed_at || 0).getTime() - new Date(b.completed_at || 0).getTime());
  const open = pending.find((t) => t.id === openId) || pending[0] || null;

  const whenText = (iso?: string | Date | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const time = d.toLocaleTimeString(current_language, { hour: '2-digit', minute: '2-digit' });
    const rel = dayRelation(d, new Date());
    if (rel === 'today') return i18n.t('approve.today_at').replace('{{time}}', time);
    const y = new Date(); y.setDate(y.getDate() - 1);
    if (dayRelation(d, y) === 'today') return i18n.t('approve.yesterday_at').replace('{{time}}', time);
    return d.toLocaleDateString(current_language, { day: 'numeric', month: 'short' }) + ' ' + time;
  };

  const approve = (task: any) => {
    flush();
    const note = notes[task.id] ?? null;
    hide(task.id);
    setOpenId(null);
    const timer = window.setTimeout(() => {
      const p = pendingRef.current;
      if (!p || p.task.id !== task.id) return;
      pendingRef.current = null;
      hideToast();
      void send(p);
    }, UNDO_MS);
    pendingRef.current = { task, note, timer };
    showToast({
      message: i18n.t('approve.approved_toast')
        .replace('{{task}}', task.title)
        .replace('{{name}}', nameOf(task.completed_by))
        .replace('{{stars}}', starsText(Number(task.points) || 0)),
      actionLabel: i18n.t('approve.undo'),
      durationMs: UNDO_MS + 500,
      onAction: () => {
        const p = pendingRef.current;
        if (p && p.task.id === task.id) {
          window.clearTimeout(p.timer);
          pendingRef.current = null;
        }
        unhide(task.id);
        setOpenId(task.id);
      },
    });
  };

  const sendBack = async (task: any, reason: Reason) => {
    setReasonFor(null);
    hide(task.id);
    try {
      await updateTask(task.id, { is_completed: false, not_done_reason: reason });
      showToast({ message: i18n.t('approve.sent_back_toast').replace('{{task}}', task.title).replace('{{name}}', nameOf(task.completed_by)) });
    } catch (e: any) {
      unhide(task.id);
      showToast({ message: errorText(e), tone: 'error' });
    }
  };

  const detail = (task: any) => {
    const who = nameOf(task.completed_by);
    const steps = (task.checklist || []).length;
    const selfBlocked = adminCount > 1 && task.completed_by === current_user.id;
    return (
      <KidCard className="flex flex-col gap-4">
        <div className="w-full h-56 md:h-72">
          <StoredPicture url={task.header_image_url} imageId={task.header_image} label={task.title} size="fill" tint="peach" fit="cover" />
        </div>
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-kid-heading font-extrabold text-2xl md:text-3xl leading-tight break-words">{task.title}</h2>
          <StarChip label={starsText(Number(task.points) || 0)} className="shrink-0" />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="flex items-center gap-2 font-bold text-lg">{initialCircle(who)} {who}</span>
          <span className="font-bold">{whenText(task.completed_at)}</span>
          {steps > 0 && (
            <span className="font-bold text-kid-green-shadow dark:text-green-300">
              ✓ {i18n.t(pluralKey('approve.steps_done', steps)).replace('{{n}}', String(steps))}
            </span>
          )}
        </div>
        {task.completion_message && <p className="text-base">“{task.completion_message}”</p>}

        {reasonFor === task.id ? (
          <div className="space-y-2">
            <p className="font-bold text-lg">{i18n.t('approve.pick_reason').replace('{{name}}', who)}</p>
            {REASONS.map((r) => (
              <KidButton key={r} size="md" variant="secondary" fullWidth onClick={() => sendBack(task, r)}>
                {i18n.t(`approve.reason_${r}`)}
              </KidButton>
            ))}
            <button type="button" onClick={() => setReasonFor(null)} className="block mx-auto min-h-[44px] px-3 font-bold underline">
              {i18n.t('approve.cancel')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" role="radiogroup">
              {NOTES.map((n) => {
                const on = notes[task.id] === n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setNotes((m) => ({ ...m, [task.id]: on ? undefined : n }))}
                    className={`min-h-[44px] px-4 rounded-full border-2 font-bold ${
                      on ? 'bg-kid-green-bg dark:bg-green-900/40 border-kid-green text-kid-green-shadow dark:text-green-200' : 'bg-kid-card dark:bg-kid-card-dark border-kid-border-strong dark:border-kid-border-strong-dark'
                    }`}
                  >
                    {i18n.t(`approve.note_${n}`)}
                  </button>
                );
              })}
            </div>
            {selfBlocked && <p className="font-bold text-red-700 dark:text-red-300">{i18n.t('approve.self_blocked')}</p>}
            <KidButton size="xl" fullWidth disabled={selfBlocked} onClick={() => approve(task)} icon={<span aria-hidden>✓</span>}>
              {i18n.t('approve.approve')}
            </KidButton>
            <KidButton size="md" variant="secondary" fullWidth className="mt-2" onClick={() => setReasonFor(task.id)}>
              {i18n.t('approve.not_done')}
            </KidButton>
          </>
        )}
      </KidCard>
    );
  };

  const row = (task: any) => (
    <li key={task.id}>
      <button
        type="button"
        onClick={() => { setOpenId(task.id); setReasonFor(null); }}
        aria-current={open?.id === task.id}
        className={`w-full flex items-center gap-3 p-3 rounded-kid-card border-2 text-left bg-kid-card dark:bg-kid-card-dark ${
          open?.id === task.id ? 'border-kid-green' : 'border-kid-border dark:border-kid-border-dark'
        }`}
      >
        <StoredPicture url={task.header_image_url} imageId={task.header_image} label={task.title} size={56} fit="cover" />
        <span className="flex-1 min-w-0">
          <span className="block font-bold text-lg truncate">{task.title}</span>
          <span className="block text-sm">{nameOf(task.completed_by)}, {whenText(task.completed_at)}, {starsText(Number(task.points) || 0)}</span>
        </span>
        <span aria-hidden>›</span>
      </button>
    </li>
  );

  const body = pending.length === 0 ? (
    <KidCard tone="mint" className="flex items-center gap-4">
      <AntMascot pose="wave" className="w-16 h-16 shrink-0" />
      <p className="font-bold text-lg">{i18n.t('parent.nothing_to_approve')}</p>
    </KidCard>
  ) : (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-8 lg:items-start">
      {/* phone/portrait: the open task first, then the others; landscape: list left */}
      <div className="order-2 lg:order-1">
        <ul className="space-y-2">{pending.filter((t) => wide || t.id !== open?.id).map(row)}</ul>
      </div>
      <div className="order-1 lg:order-2">{open && detail(open)}</div>
    </div>
  );

  if (embedded) return <div className="text-kid-text dark:text-kid-text-dark" lang={current_language}>{body}</div>;

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => navigate('/home')}
          aria-label={i18n.t('approve.back')}
          className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold flex items-center justify-center shrink-0"
        >
          <span aria-hidden>←</span>
        </button>
        <h1 className="flex-1 font-kid-heading font-extrabold text-3xl md:text-4xl">{i18n.t('approve.title')}</h1>
        {pending.length > 0 && (
          <span className="min-w-[44px] h-11 px-3 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border flex items-center justify-center font-kid-heading font-extrabold text-xl">
            {pending.length}
          </span>
        )}
      </div>
      {body}
    </div>
  );
};

export default ApproveTasks;
